import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUp, Paperclip, Square, X } from 'lucide-react';
import type { AssistantUploadView } from '@/hooks/assistant/useAssistantUploads';
import { cn } from '@/lib/utils';
import { UploadStatusBadge } from './UploadStatusBadge';

const MAX_HEIGHT_PX = 200;

interface ComposerProps {
  isOpen: boolean;
  isRunning: boolean;
  isDisabled: boolean;
  attachments: AssistantUploadView[];
  draft: string;
  onDraftChange: (value: string) => void;
  onSend: (message: string) => void;
  onStop: () => void;
  onAttach: (files: File[]) => void;
  onRemoveAttachment: (id: string) => void;
}

export function Composer({
  isOpen,
  isRunning,
  isDisabled,
  attachments,
  draft,
  onDraftChange,
  onSend,
  onStop,
  onAttach,
  onRemoveAttachment,
}: ComposerProps) {
  const { t } = useTranslation('adminAssistant');
  const inputId = useId();
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [isComposing, setIsComposing] = useState(false);

  useEffect(() => {
    if (!isOpen || !window.matchMedia('(pointer: fine)').matches) return;
    const timer = setTimeout(() => textareaRef.current?.focus(), 320);
    return () => clearTimeout(timer);
  }, [isOpen]);

  useEffect(() => {
    const element = textareaRef.current;
    if (!element) return;
    element.style.height = 'auto';
    element.style.height = `${Math.min(element.scrollHeight, MAX_HEIGHT_PX)}px`;
  }, [draft]);

  const canSend =
    !isRunning &&
    !isDisabled &&
    (draft.trim().length > 0 || attachments.length > 0);

  const submit = () => {
    if (!canSend) return;
    onSend(draft.trim());
  };

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
      className="shrink-0 border-t border-border/50 bg-card/80 p-3"
    >
      {attachments.length > 0 && (
        <ul className="mb-2 flex flex-wrap gap-1.5">
          {attachments.map((upload) => (
            <li
              key={upload.id}
              className="flex max-w-full items-center gap-1.5 rounded-lg border border-border/50 bg-secondary/50 py-1 pr-1 pl-2 text-xs"
            >
              <span className="max-w-[160px] truncate">{upload.filename}</span>
              <UploadStatusBadge
                status={upload.status}
                progress={upload.local?.progress}
              />
              <button
                type="button"
                onClick={() => onRemoveAttachment(upload.id)}
                aria-label={t('composer.removeAttachment')}
                className="rounded p-0.5 text-muted-foreground hover:bg-background/60 hover:text-foreground"
              >
                <X className="h-3 w-3" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-end gap-2 rounded-2xl border border-border/60 bg-background/60 p-1.5 transition-colors focus-within:border-primary/50">
        <input
          ref={fileRef}
          type="file"
          multiple
          className="hidden"
          accept="video/*,image/*,.pdf,.zip,.rar,.doc,.docx,.xls,.xlsx"
          onChange={(event) => {
            const files = Array.from(event.target.files ?? []);
            if (files.length) onAttach(files);
            event.target.value = '';
          }}
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={isDisabled}
          aria-label={t('composer.attach')}
          title={t('composer.attach')}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-muted-foreground transition-colors hover:bg-secondary/80 hover:text-foreground disabled:opacity-40"
        >
          <Paperclip className="h-4 w-4" />
        </button>
        <label htmlFor={inputId} className="sr-only">
          {t('composer.label')}
        </label>
        <textarea
          id={inputId}
          ref={textareaRef}
          rows={1}
          value={draft}
          disabled={isDisabled}
          onChange={(event) => onDraftChange(event.target.value)}
          onCompositionStart={() => setIsComposing(true)}
          onCompositionEnd={() => setIsComposing(false)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey && !isComposing) {
              event.preventDefault();
              submit();
            }
          }}
          placeholder={
            isRunning
              ? t('composer.placeholderRunning')
              : t('composer.placeholder')
          }
          className="max-h-[200px] min-h-[36px] flex-1 resize-none bg-transparent px-1 py-2 text-sm leading-5 text-foreground outline-hidden placeholder:text-muted-foreground disabled:opacity-60"
        />
        {isRunning ? (
          <button
            type="button"
            onClick={onStop}
            aria-label={t('composer.stop')}
            title={t('composer.stop')}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-secondary text-foreground transition-colors hover:bg-secondary/80"
          >
            <Square className="h-3.5 w-3.5 fill-current" />
          </button>
        ) : (
          <button
            type="submit"
            disabled={!canSend}
            aria-label={t('composer.send')}
            title={t('composer.send')}
            className={cn(
              'flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground transition-opacity',
              !canSend && 'opacity-40',
            )}
          >
            <ArrowUp className="h-4 w-4" />
          </button>
        )}
      </div>
    </form>
  );
}
